import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Group, Modal, Stack, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { transitionVisitStatus, updateVisitData } from '../../api/resources';
import { qaCheckName, readVisitQa } from '../../lib/qaChecks';
import type { VisitQa } from '../../types/api';
import { VisitStatus } from '../../types/domain';

/**
 * Rejects the visit and records why. Sending an officer back is a separate
 * action — Retry visit — which creates a new visit and leaves this one as it is.
 */
export function RejectVisitModal({
    opened,
    onClose,
    visitId,
    qa,
    onDone,
}: {
    opened: boolean;
    onClose: () => void;
    visitId: string;
    /** The visit's automated QA; its failed checks are offered as reasons. */
    qa?: VisitQa | null;
    onDone?: () => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [reason, setReason] = useState('');
    const [pickedChecks, setPickedChecks] = useState<string[]>([]);
    const failedChecks = readVisitQa(qa).checks.filter((c) => c.status === 'failed');
    // The checks QA agreed with lead the reason; anything typed is added after.
    const fullReason = [
        ...failedChecks.filter((c) => pickedChecks.includes(c.key)).map((c) => qaCheckName(t, c.key)),
        reason.trim(),
    ]
        .filter(Boolean)
        .join('; ');
    const [comments, setComments] = useState('');

    const rejectMutation = useMutation({
        mutationFn: async () => {
            await transitionVisitStatus(visitId, { toStatus: VisitStatus.Rejected });

            const trimmedComments = comments.trim();
            if (fullReason || trimmedComments) {
                await updateVisitData(visitId, {
                    ...(fullReason ? { rejectionReason: fullReason } : {}),
                    ...(trimmedComments ? { rejectionComments: trimmedComments } : {}),
                });
            }
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.rejected') });
            onClose();
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
            void queryClient.invalidateQueries({ queryKey: ['assignment-queue'] });
            onDone?.();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('features.visits.rejectTitle')}
            size="lg"
        >
            <Stack>
                {failedChecks.length > 0 ? (
                    <Checkbox.Group
                        label={t('features.visits.qa.rejectChecksLabel')}
                        description={t('features.visits.qa.rejectChecksHint')}
                        value={pickedChecks}
                        onChange={setPickedChecks}
                    >
                        <Stack gap="xs" mt="xs">
                            {failedChecks.map((c) => (
                                <Checkbox
                                    key={c.key}
                                    value={c.key}
                                    label={qaCheckName(t, c.key)}
                                    description={c.reason}
                                />
                            ))}
                        </Stack>
                    </Checkbox.Group>
                ) : null}
                <TextInput
                    label={t('features.visits.rejectionReason')}
                    placeholder={t('features.visits.rejectionReasonPlaceholder')}
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                />
                <Textarea
                    label={t('features.visits.rejectionComments')}
                    minRows={2}
                    value={comments}
                    onChange={(e) => setComments(e.currentTarget.value)}
                />

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button
                        color="red"
                        loading={rejectMutation.isPending}
                        onClick={() => rejectMutation.mutate()}
                    >
                        {t('features.visits.rejectAction')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
